// Per-type variable + column-key registry (client mirror).
//
// Mirrors `server/src/modules/printTemplates/printTemplates.variables.ts`
// exactly. The variable palette UI uses these definitions; the
// builder validates `ColumnDef.key` membership against
// `getColumnKeysForType(type)`.
//
// Kept in lockstep with the server — a vitest unit test asserts
// the keys match (any divergence is a bug).

import type { PrintTemplateType } from '../../../types/api';

export interface VariableDefinition {
  /** Without braces — e.g. `'company_name'`. */
  token: string;
  /** Human label for the palette chip (e.g. "Company name"). */
  label: string;
  /** One-line description shown beneath the chip on hover. */
  description: string;
}

export interface ColumnKeyDefinition {
  /** Used as `ColumnDef.key`. */
  key: string;
  /** Default label (admin can override). */
  label: string;
  /** Default formatter (admin can override). */
  format: 'plain' | 'currency' | 'date_short';
}

// ---------------------------------------------------------------------------
// Company / generation variables — shared by every template_type.
// ---------------------------------------------------------------------------

export const COMPANY_VARIABLES: VariableDefinition[] = [
  {
    token: 'company_name',
    label: 'Company name',
    description: 'Tenant company display name (companies.name).',
  },
  {
    token: 'company_logo',
    label: 'Company logo',
    description:
      'Renders only inside the header logo slot — not as text. Hidden when no logo is configured.',
  },
  {
    token: 'company_address',
    label: 'Company address',
    description: 'Free-form multiline address (companies.address).',
  },
  {
    token: 'company_phone',
    label: 'Company phone',
    description: 'Tenant contact phone number.',
  },
  {
    token: 'company_email',
    label: 'Company email',
    description: 'Tenant contact email.',
  },
  {
    token: 'company_subdomain',
    label: 'Company subdomain',
    description: 'e.g. `acme` from acme.tricivo.com.',
  },
  {
    token: 'generated_at',
    label: 'Generated at',
    description: 'Timestamp when the print/PDF was generated (server clock).',
  },
  {
    token: 'generated_by',
    label: 'Generated by',
    description: 'Display name of the staff member who triggered the render.',
  },
];

// ---------------------------------------------------------------------------
// diary_results — per-type header/footer scalars.
// ---------------------------------------------------------------------------

export const DIARY_RESULTS_HEADER_VARIABLES: VariableDefinition[] = [
  {
    token: 'driver_name',
    label: 'Driver name',
    description: 'Name of the driver this diary belongs to.',
  },
  {
    token: 'week_start_date',
    label: 'Week starting',
    description: 'Monday of the diary week (formatted).',
  },
  {
    token: 'week_end_date',
    label: 'Week ending',
    description: 'Sunday of the diary week (formatted).',
  },
  {
    token: 'total_bookings',
    label: 'Total bookings',
    description: 'Booking count across the week.',
  },
  {
    token: 'total_revenue',
    label: 'Total revenue',
    description: 'Sum of booking prices across the week (£).',
  },
];

// ---------------------------------------------------------------------------
// diary_results — per-row body column keys (used as ColumnDef.key).
// ---------------------------------------------------------------------------

export const DIARY_RESULTS_COLUMN_KEYS: ColumnKeyDefinition[] = [
  { key: 'display_order', label: '#', format: 'plain' },
  { key: 'day_of_week', label: 'Day', format: 'plain' },
  { key: 'date', label: 'Date', format: 'date_short' },
  { key: 'car_registration', label: 'Reg', format: 'plain' },
  { key: 'car_make_model', label: 'Vehicle', format: 'plain' },
  { key: 'price', label: 'Price', format: 'currency' },
  { key: 'source_name', label: 'Source', format: 'plain' },
  { key: 'product_name', label: 'Product', format: 'plain' },
  { key: 'address', label: 'Address', format: 'plain' },
  { key: 'postcode', label: 'Postcode', format: 'plain' },
  { key: 'customer_name', label: 'Customer', format: 'plain' },
  { key: 'customer_phone', label: 'Phone', format: 'plain' },
  { key: 'customer_email', label: 'Email', format: 'plain' },
  { key: 'payment_mode', label: 'Payment', format: 'plain' },
  { key: 'timeframe', label: 'Time', format: 'plain' },
  { key: 'notes', label: 'Notes', format: 'plain' },
  { key: 'status', label: 'Status', format: 'plain' },
  { key: 'reference', label: 'Reference', format: 'plain' },
];

// ---------------------------------------------------------------------------
// receipt — per-type header/footer/body scalars (custom_html mode).
//
// Receipts have no flat-table body, so the per-row registry stays
// empty; every receipt-specific token below is interpolated through
// the header / footer / custom_html slots once Session B lands.
// ---------------------------------------------------------------------------

export const RECEIPT_HEADER_VARIABLES: VariableDefinition[] = [
  {
    token: 'unique_id',
    label: 'Receipt ID',
    description: 'Receipt unique reference (receipts.unique_id).',
  },
  {
    token: 'date',
    label: 'Date',
    description: 'Receipt date — receipts.created_at formatted (date_short).',
  },
  {
    token: 'customer_name',
    label: 'Customer name',
    description: 'Customer display name (customers.name).',
  },
  {
    token: 'customer_phone',
    label: 'Customer phone',
    description: 'Customer contact phone (customers.phone).',
  },
  {
    token: 'customer_email',
    label: 'Customer email',
    description: 'Customer contact email (customers.email).',
  },
  {
    token: 'customer_address',
    label: 'Customer address',
    description: 'Free-text customer address (customers.address).',
  },
  {
    token: 'customer_postcode',
    label: 'Customer postcode',
    description: 'Customer postcode (customers.postcode).',
  },
  {
    token: 'amount',
    label: 'Amount',
    description: 'Receipt amount (receipts.amount, currency-formatted).',
  },
  {
    token: 'payment_mode',
    label: 'Payment method',
    description: 'Payment mode label (receipts.payment_mode).',
  },
  {
    token: 'car_registration',
    label: 'Vehicle registration',
    description: 'Vehicle registration (receipts.car_registration).',
  },
  {
    token: 'car_make_model',
    label: 'Make & model',
    description: 'Vehicle make & model (receipts.car_make_model).',
  },
  {
    token: 'chassis',
    label: 'Chassis number',
    description: 'Chassis / VIN number (receipts.chassis).',
  },
  {
    token: 'car_color',
    label: 'Vehicle colour',
    description: 'Vehicle colour (receipts.car_color).',
  },
  {
    token: 'source_name',
    label: 'Source',
    description: 'Lead source label (sources.name).',
  },
  {
    token: 'mot_value',
    label: 'MOT',
    description: 'Yes / No — whether MOT documents were handed over (receipts.mot).',
  },
  {
    token: 'logbook_value',
    label: 'Log book',
    description: 'Yes / No — whether the log book was handed over (receipts.logbook).',
  },
  {
    token: 'keys_value',
    label: 'Keys',
    description: 'Yes / No — whether keys were handed over (receipts.keys).',
  },
  {
    token: 'company_website',
    label: 'Company website',
    description: 'Tenant website URL (companies.website).',
  },
  {
    token: 'generated_date',
    label: 'Generated date',
    description: 'Date the receipt was rendered (server clock, formatted).',
  },
];

// ---------------------------------------------------------------------------
// Per-type registries (invoice still empty for v1).
// ---------------------------------------------------------------------------

const HEADER_VARIABLES_BY_TYPE: Record<PrintTemplateType, VariableDefinition[]> = {
  diary_results: DIARY_RESULTS_HEADER_VARIABLES,
  receipt: RECEIPT_HEADER_VARIABLES,
  invoice: [],
  // WB types — variable registries live server-side in
  // server/src/modules/wb/print-templates/variables.ts. The client-side
  // builder treats them as opaque (no token autocomplete yet); revisit
  // when the WB builder UX is added.
  wb_ticket: [],
  wb_advice_note: [],
  wb_wtn: [],
  wb_hazardous_consignment: [],
};

const COLUMN_KEYS_BY_TYPE: Record<PrintTemplateType, ColumnKeyDefinition[]> = {
  diary_results: DIARY_RESULTS_COLUMN_KEYS,
  receipt: [],
  invoice: [],
  wb_ticket: [],
  wb_advice_note: [],
  wb_wtn: [],
  wb_hazardous_consignment: [],
};

/**
 * Full set of header/footer variables available for a given type.
 * Concatenation: company variables + per-type header scalars.
 */
export function getVariablesForType(type: PrintTemplateType): VariableDefinition[] {
  return [...COMPANY_VARIABLES, ...HEADER_VARIABLES_BY_TYPE[type]];
}

/**
 * Set of body-column keys the type's BodyEditor lets the admin pick
 * from. Empty array means "no columns supported yet" (receipt +
 * invoice in v1).
 */
export function getColumnKeysForType(type: PrintTemplateType): ColumnKeyDefinition[] {
  return COLUMN_KEYS_BY_TYPE[type];
}

/** Lookup helper for the renderer + tests. */
export function findColumnKeyDefinition(
  type: PrintTemplateType,
  key: string,
): ColumnKeyDefinition | undefined {
  return COLUMN_KEYS_BY_TYPE[type].find((c) => c.key === key);
}
